import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { Typeahead } from "react-bootstrap-typeahead";
import {
  Row,
  Col,
  ButtonGroup,
  Dropdown,
  Modal,
  Button,
  Table,
  Form,
} from "react-bootstrap";
import { searchLoincWithLimit } from "../services/loincCodes-service";
import {
  addNewReportingLOINC,
  fetchReportingLoincById,
  updateReportingLOINCData,
} from "../services/reportingLoinc-service";
import { handleIsActive, validateAndSanitizeUrl } from "../util/common";
import { fetchCodeSetTable } from "../services/referenceTable-service";
import RatioTextField from "./common/RatioTextField";
import {
  S3_URL,
  OPERATORS_WITH_RATO,
  TEST_RESULT_OPTIONS,
} from "../services/Constants";

const initialInterpretation = [
  {
    qnInterpretationConditions: [
      {
        sourceObject: "QNInterpretation",
        sourceField: "value1",
        operator: "",
        valueRHS: "",
        seqNum: "1",
        logicalOperator: "",
        rowNum: 1,
      },
      {
        sourceObject: "QNInterpretation",
        sourceField: "value2",
        operator: "",
        valueRHS: "",
        seqNum: "2",
        logicalOperator: "&&",
        rowNum: 1,
      },
    ],
    qnInterpretationActions: [
      {
        sourceObject: "QNInterpretation",
        sourceField: "output",
        operator: "Equal to",
        valueRHS: "",
        seqNum: "1",
        logicalOperator: "",
        rowNum: 1,
      },
    ],
  },
];

const ReportingLOINCAddUpdateModal = (props) => {
  const {
    show,
    handleClose,
    setIsError,
    setIsAdded,
    setIsUpdated,
    reportingLoincId,
    isAddNew,
    setModifiedRow,
    childFormRef,
    loincId,
    performingFacilityData,
  } = props;

  const [form, setForm] = useState({});
  const [validated, setValidated] = useState(false);
  const [activeLoincs, setActiveLoincs] = useState([]);
  const [qnInterpetationChecked, setQnInterpetationChecked] = useState(false);
  const [unitData, setUnitData] = useState([]);
  const [errorText, setErrorText] = useState("");
  const [isStandardLoincSelected, setIsStandardLoincSelected] = useState(true);
  const [loading, setLoading] = useState(false);

  const [interpretations, setInterpretations] = useState(
    JSON.parse(JSON.stringify(initialInterpretation))
  );

  const setField = (field, value) => {
    setForm({
      ...form,
      [field]: value,
    });
  };

  const setQnInterpretaionsField = (field, value) => {
    setForm({
      ...form,
      qnInterpretations: {
        ...form?.qnInterpretations,
        [field]: value,
      },
    });
  };

  const getUnitDropdownValues = async () => {
    let response = await fetchCodeSetTable("Units of Measure");
    if (response.ok) {
      let data = await response.json();
      setUnitData(data.refTableValues);
    }
  };

  const CustomToggleNull = React.forwardRef(({ children, onClick }, ref) => (
    <a
      href=""
      className="blackTextNullModal"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      &#x22EE;
    </a>
  ));

  const CustomToggleText = React.forwardRef(({ children, onClick }, ref) => (
    <a
      href=""
      className="blackTextonTextModal"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      &#x22EE;
    </a>
  ));

  const CustomToggleRatio = React.forwardRef(({ children, onClick }, ref) => (
    <a
      href=""
      className="blackTextRatio"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      &#x22EE;
    </a>
  ));


  const getReportingLOINCInfo = async () => {
    let response = await fetchReportingLoincById(reportingLoincId);
    if (response.ok) {
      let data = await response.json();
      setForm(data);
      setInterpretations(
        data?.qnInterpretations?.qnInterpretationList
          ? data?.qnInterpretations?.qnInterpretationList
          : initialInterpretation
      );
      setQnInterpetationChecked(data?.qnInterpretation);
      setActiveLoincs([data?.loinc]);
    }
  };

  const clearForm = () => {
    fetchLoincs("");
    setForm({});
    setQnInterpetationChecked(false);
    setInterpretations(JSON.parse(JSON.stringify(initialInterpretation)));
  };

  const addReportingLOINC = async () => {
    let data = await addNewReportingLOINC(form);
    if (data.reportingLoincId != null) {
      setIsAdded(true);
      setModifiedRow(data?.reportingLoincCode);
    } else {
      setIsError(true);
    }
  };

  const updateReportingLOINC = async () => {
    let data = await updateReportingLOINCData(form);
    if (data.reportingLoincId != null) {
      setIsUpdated(true);
      setModifiedRow(data?.reportingLoincCode);
    } else {
      setIsError(true);
    }
  };

  const getSelectedFacility = () => {
    if(performingFacilityData === undefined) return [];

    let facility = performingFacilityData?.filter(
      (item) => item.performingFacilityId === form.performingFacilityId
    );
    return facility?.length === 0 ? [] : facility;
  };

  const getSelectedStandardLoinc = () => {
    if (activeLoincs === undefined) return [];

    let standardLoinc = activeLoincs?.filter(
      (item) => item.loincId === form.standardLoincId
    );
    return standardLoinc?.length === 0 ? [] : standardLoinc;
  };

  const fetchLoincs = useCallback(
    _.debounce(async (query) => {
      setLoading(true);
      try {
        const response = await searchLoincWithLimit(50, query);
        const data = await response.json();
        setActiveLoincs(data);
      } catch (err) {
        console.error("Error fetching loincs:", err);
      } finally {
        setLoading(false);
      }
    }, 500),
    []
  );

  const handleSubmit = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!qnInterpetationChecked && !form?.reportingLoincCode?.length) {
      setErrorText(
        'Either enter "Reporting LOINC Code" or enter "Qn Interpretation"!'
      );
      return;
    } else {
      setErrorText("");
    }

    if (loincId !== undefined) form.standardLoincId = loincId;

    if (!form.standardLoincId) {
      setIsStandardLoincSelected(false)
      return
    }

    setValidated(true);

    form.qnInterpretation = qnInterpetationChecked;
    if (qnInterpetationChecked) {
      if (!form.qnInterpretations) {
        form.qnInterpretations = {};
      }
      form.qnInterpretations.qnInterpretationList = interpretations;
    }

    if (event.currentTarget.checkValidity()) {
      isAddNew ? addReportingLOINC() : updateReportingLOINC();
      handleClose();
      setIsStandardLoincSelected(true)
      setValidated(false);
      setForm({});
      setInterpretations(JSON.parse(JSON.stringify(initialInterpretation)));
    }
  };

  const findMax = (copyInterpretations) => {
    let max = 0;
    copyInterpretations.map((interpretation) => {
      if (interpretation?.qnInterpretationActions[0]?.rowNum > max)
        max = interpretation?.qnInterpretationActions[0]?.rowNum;
    });
    return max;
  };

  const addFields = () => {
    let copy = JSON.parse(JSON.stringify(initialInterpretation[0])); //Deep copy

    let maxRowNum = findMax(interpretations);
    copy.qnInterpretationConditions[0].rowNum = maxRowNum + 1;
    copy.qnInterpretationConditions[1].rowNum = maxRowNum + 1;
    copy.qnInterpretationActions[0].rowNum = maxRowNum + 1;
    copy.qnInterpretationConditions[0].operator =
      interpretations[0].qnInterpretationConditions.find(
        (obj) => obj.seqNum == 1
      )?.operator;
    copy.qnInterpretationConditions[1].operator =
      interpretations[0].qnInterpretationConditions.find(
        (obj) => obj.seqNum == 2
      )?.operator;

    let updatedInterpretations = [...interpretations].concat(copy);
    setInterpretations(updatedInterpretations);
  };

  const removeRow = (interpretation, index) => {
    let copyInterpretations = [...interpretations];
    if (copyInterpretations.length === 1)
      copyInterpretations = [...initialInterpretation];
    else
      copyInterpretations = copyInterpretations.filter(
        (obj) =>
          obj?.qnInterpretationActions[0]?.rowNum !==
          interpretation?.qnInterpretationActions[0]?.rowNum
      );

    setInterpretations(copyInterpretations);
  };

  const setOperator = (seqNum, value) => {
    let copyInterpretations = [...interpretations];

    copyInterpretations.forEach((obj) => {
      let Y = obj.qnInterpretationConditions.find(
        (obj) => obj.seqNum == seqNum
      );
      Y["valueRHS"] = "";
      Y["operator"] = value;
    });
    setInterpretations(copyInterpretations);
  };

  const updateInputDecision = (interpretation, index, seqNum, value) => {
    let copyInterpretations = [...interpretations];

    let X = copyInterpretations[index];

    let Y = X.qnInterpretationConditions.find((obj) => obj.seqNum == seqNum);
    Y["valueRHS"] = value;

    setInterpretations(copyInterpretations);
  };

  const updateInputDecisionOutput = (interpretation, index, value) => {
    let copyInterpretations = [...interpretations];
    let X = copyInterpretations[index];
    let Y = X.qnInterpretationActions[0];
    Y["valueRHS"] = value;
    setInterpretations(copyInterpretations);
  };

  useEffect(() => {
    if (show) {
      isAddNew ? clearForm() : getReportingLOINCInfo();
    } else {
      clearForm();
    }
  }, [show, isAddNew]);

  useEffect(() => {
    form.standardLoincId = loincId;
  }, []);

  useEffect(() => {
    getUnitDropdownValues();
  }, []);

  const validateDecisionTable = () => {
    let required = false;
    interpretations.forEach((item) => {
      if (
        item.qnInterpretationConditions[0].valueRHS !== "" ||
        item.qnInterpretationConditions[1].valueRHS !== "" ||
        item.qnInterpretationConditions[0].operator !== "" ||
        item.qnInterpretationConditions[1].operator !== "" ||
        item.qnInterpretationActions[0].valueRHS !== ""
      ) {
        required = true;
      }
    });
    return required;
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Form
        ref={childFormRef}
        onSubmit={handleSubmit}
        noValidate
        validated={validated}
      >
        <Modal.Header className="modalHeaderContainer" closeButton>
          <Modal.Title className="modalHeader generalFont">
            {isAddNew ? "Add" : "Update"} Reporting LOINC
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyContainer">
          <Row>
            <Col>
              <Form.Group
                className="mb-3"
                controlId="form.performingFacilityId"
              >
                <Form.Label className="modalLabel generalFont">
                  Performing Facility
                </Form.Label>
                <Typeahead
                  id="basic-typeahead-single"
                  labelKey={(option) =>
                    `${option?.performingFacilityName}-${option?.clia}`
                  }
                  onChange={(selected) => {
                    setField(
                      "performingFacilityId",
                      selected[0]?.performingFacilityId
                    );
                  }}
                  selected={getSelectedFacility()}
                  options={_.sortBy(performingFacilityData, [
                    "performingFacilityName",
                  ])}
                  placeholder="Choose or search facility"
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="form.reportingLoincCode">
                <React.Fragment>
                  <Form.Label className="modalLabel generalFont">
                    Reporting LOINC Code{" "}
                  </Form.Label>
                </React.Fragment>
                <Form.Control
                  className="modalInput generalFont"
                  type="text"
                  placeholder="Hint Text"
                  value={form.reportingLoincCode}
                  maxLength={250}
                  onChange={(e) =>
                    setField("reportingLoincCode", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId="form.standardLoincId">
                <Form.Label className="modalLabel generalFont">
                  Standard LOINC <i className="text-danger">(required)</i>
                </Form.Label>
                <Typeahead
                  id="basic-typeahead-single"
                  labelKey={(option) =>
                    `${option?.loincCode}, ${option?.loincLongName}`
                  }
                  onChange={(selected) => {
                    setField("standardLoincId", selected[0]?.loincId);
                  }}
                  onInputChange={(query) => fetchLoincs(query)}
                  selected={getSelectedStandardLoinc()}
                  options={_.sortBy(activeLoincs, ["loincCode"])}
                  disabled={loincId !== undefined}
                  required
                  isInvalid={!isStandardLoincSelected && !form.standardLoincId}
                  placeholder="Choose or search standard LOINC"
                  isLoading={loading || !activeLoincs || activeLoincs.length === 0}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="form.isActive">
                <Form.Label className="modalLabel generalFont">
                  Status <i className="text-danger">(required)</i>
                </Form.Label>
                <Form.Select
                  className="modalInput generalFont"
                  aria-label="Choose Item"
                  required
                  value={handleIsActive(form.isActive)}
                  onChange={(e) =>
                    setField(
                      "isActive",
                      e.target.value === "Active" ? true : false
                    )
                  }
                >
                  <option hidden value="">
                    Choose Item
                  </option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <Form.Group
                className="mb-3"
                controlId="form.resultInterpretation"
              >
                <Form.Label className="modalLabel generalFont">
                  Result Interpretation
                </Form.Label>
                <Form.Select
                  className="modalInput generalFont"
                  aria-label="Choose Item"
                  value={form?.resultInterpretation}
                  onChange={(e) =>
                    setField("resultInterpretation", e.target.value)
                  }
                  feedback="You must select before submitting."
                >
                  <option hidden value="">
                    Choose Item
                  </option>
                  <option value=""> </option>
                  <option value="Set by OBX2">Set By OBX2</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Form.Check
              style={{
                marginLeft: "10px",
                marginTop: "10px",
                marginBottom: "20px",
              }}
              name="qnInterpetationChecked"
              value={qnInterpetationChecked}
              type="checkbox"
              label="Qn Interpretation"
              inline
              onChange={(e) => {
                setQnInterpetationChecked(e.target.checked);
              }}
              checked={qnInterpetationChecked}
            />
          </Row>
          <Row>
            <Col>
              <Form.Group
                className="mb-3"
                controlId="form.qnInterpretations.units"
              >
                <React.Fragment>
                  <Form.Label className="modalLabel generalFont">
                    Units
                  </Form.Label>
                </React.Fragment>
                <Form.Select
                  disabled={!qnInterpetationChecked}
                  className="modalInput generalFont"
                  aria-label="Choose Item"
                  value={
                    !qnInterpetationChecked
                      ? ""
                      : form?.qnInterpretations?.units
                  }
                  onChange={(e) =>
                    setQnInterpretaionsField("units", e.target.value)
                  }
                  feedback="You must select before submitting."
                >
                  <option hidden value="">
                    Choose Item
                  </option>
                  {_.sortBy(unitData, ["reference_code"]).map((entry) => {
                    return (
                      <option
                        key={entry.referenceTableValueId}
                        value={entry.reference_code}
                      >
                        {entry.reference_code}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group
                className="mb-3"
                controlId="form.qnInterpretations.referenceRange"
              >
                <React.Fragment>
                  <Form.Label className="modalLabel generalFont">
                    Reference Range
                  </Form.Label>
                </React.Fragment>
                <Form.Control
                  disabled={!qnInterpetationChecked}
                  className="modalInput generalFont"
                  type="text"
                  placeholder="Enter Data"
                  maxLength={20}
                  value={
                    !qnInterpetationChecked
                      ? ""
                      : form?.qnInterpretations?.referenceRange
                  }
                  onChange={(e) =>
                    setQnInterpretaionsField("referenceRange", e.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group
                className="mb-3"
                controlId="form.qnInterpretations.bloqInterpretation"
              >
                <Form.Label className="modalLabel generalFont">
                  BLOQ Interpretation{" "}
                </Form.Label>
                <Form.Select
                  disabled={!qnInterpetationChecked}
                  className="modalInput generalFont"
                  aria-label="Choose Item"
                  value={
                    !qnInterpetationChecked
                      ? ""
                      : form?.qnInterpretations?.bloqInterpretation
                  }
                  onChange={(e) =>
                    setQnInterpretaionsField(
                      "bloqInterpretation",
                      e.target.value
                    )
                  }
                  feedback="You must select before submitting."
                >
                  <option hidden value="">
                    Result
                  </option>
                  {TEST_RESULT_OPTIONS.sort((a, b) => (a > b ? 1 : -1)).map(
                    (entry) => {
                      return (
                        <option key={entry} value={entry}>
                          {entry}
                        </option>
                      );
                    }
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group
                className="mb-3"
                controlId="form.qnInterpretations.aloqInterpretation"
              >
                <Form.Label className="modalLabel generalFont">
                  ALOQ Interpretation{" "}
                </Form.Label>
                <Form.Select
                  disabled={!qnInterpetationChecked}
                  className="modalInput generalFont"
                  aria-label="Choose Item"
                  value={
                    !qnInterpetationChecked
                      ? ""
                      : form?.qnInterpretations?.aloqInterpretation
                  }
                  onChange={(e) =>
                    setQnInterpretaionsField(
                      "aloqInterpretation",
                      e.target.value
                    )
                  }
                  feedback="You must select before submitting."
                >
                  <option hidden value="">
                    Result
                  </option>
                  {TEST_RESULT_OPTIONS.sort((a, b) => (a > b ? 1 : -1)).map(
                    (entry) => {
                      return (
                        <option key={entry} value={entry}>
                          {entry}
                        </option>
                      );
                    }
                  )}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          {qnInterpetationChecked && (
            <Row style={{ marginTop: "15px" }}>
              <Form.Group
                className="mb-3"
                controlId="form.qnInterpretations.qnInterpretations"
              >
                <div className="cardBody">
                  <img
                    src={`${validateAndSanitizeUrl(S3_URL)}rules-details-icon.png`}
                    style={{
                      width: "20px",
                      height: "18px",
                      marginRight: "15px",
                    }}
                    alt=""
                  ></img>
                  Qn Interpretation Decision Table
                </div>
              </Form.Group>
              <div
                className="tableContainerNoScroll"
                style={{ marginBottom: "10px" }}
              >
                <Table className="tableN" bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ paddingLeft: "15px" }}>Value</th>
                      <th style={{ paddingLeft: "15px" }}>Value</th>
                      <th style={{ minWidth: "150px", paddingLeft: "15px" }}>
                        Result(Output)
                      </th>
                      <th style={{ width: "50px" }}></th>
                    </tr>
                    <tr>
                      <th width="10%" className="decisionTableTh min-width-150">
                        {
                          <Form.Select
                            name="operator"
                            className="decisionTableCellInput"
                            aria-label="Operator"
                            required={validateDecisionTable()}
                            value={
                              interpretations[0]?.qnInterpretationConditions?.find(
                                (obj) => obj.seqNum == 1
                              )?.operator
                            }
                            onChange={(event) =>
                              setOperator(1, event.target.value)
                            }
                          >
                            <option value="">Operator</option>
                            {OPERATORS_WITH_RATO.map((operator) => (
                              <option value={operator}>{operator}</option>
                            ))}
                          </Form.Select>
                        }
                      </th>{" "}
                      <th width="10%" className="decisionTableTh min-width-150">
                        {
                          <Form.Select
                            required={validateDecisionTable()}
                            name="operator"
                            className="decisionTableCellInput"
                            aria-label="Operator"
                            value={
                              interpretations[0]?.qnInterpretationConditions?.find(
                                (obj) => obj.seqNum == 2
                              )?.operator
                            }
                            onChange={(event) =>
                              setOperator(2, event.target.value)
                            }
                          >
                            <option value="">Operator</option>{" "}
                            {OPERATORS_WITH_RATO.map((operator) => (
                              <option value={operator}>{operator}</option>
                            ))}
                          </Form.Select>
                        }
                      </th>
                      <th className="decisionTableTh min-width-150"></th>
                      <th
                        className="decisionTableTh"
                        style={{ width: "50px" }}
                      ></th>
                    </tr>
                  </thead>
                  <tbody>
                    {interpretations
                      .sort(
                        (a, b) =>
                          a?.qnInterpretationActions[0]?.rowNum -
                          b?.qnInterpretationActions[0]?.rowNum
                      )
                      .map((interpretation, index) => {
                        return (
                          <tr>
                            <td
                              style={{
                                backgroundColor:
                                  interpretation.qnInterpretationConditions.find(
                                    (obj) => obj.seqNum == 1
                                  )?.valueRHS === null
                                    ? "#d1d1d1"
                                    : "white",
                              }}
                            >
                              <Dropdown as={ButtonGroup} autoClose="outside">
                                {interpretation.qnInterpretationConditions.find(
                                  (obj) => obj.seqNum == 1
                                )?.valueRHS === null ? (
                                  <>
                                    <Form.Text className="decisionTableCellInput">
                                      Null
                                    </Form.Text>
                                    <Dropdown.Toggle
                                      as={CustomToggleNull}
                                      split
                                    />
                                  </>
                                ) : (
                                  <>
                                    {interpretation.qnInterpretationConditions
                                      .find((obj) => obj.seqNum == 1)
                                      ?.operator.includes("Ratio") ? (
                                      <>
                                        <RatioTextField
                                          required={validateDecisionTable()}
                                          name={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 1
                                            )?.sourceField
                                          }
                                          value={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 1
                                            )?.valueRHS
                                          }
                                          onChange={(value) =>
                                            updateInputDecision(
                                              interpretation,
                                              index,
                                              1,
                                              value
                                            )
                                          }
                                        />{" "}
                                        <Dropdown.Toggle
                                          as={CustomToggleRatio}
                                          split
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <Form.Control
                                          required={validateDecisionTable()}
                                          className="decisionTableCellInput"
                                          placeholder="Enter Value"
                                          name={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 1
                                            )?.sourceField
                                          }
                                          value={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 1
                                            )?.valueRHS
                                          }
                                          onChange={(event) =>
                                            updateInputDecision(
                                              interpretation,
                                              index,
                                              1,
                                              event.target.value
                                            )
                                          }
                                        />
                                        <Dropdown.Toggle
                                          as={CustomToggleText}
                                          split
                                        />
                                      </>
                                    )}
                                  </>
                                )}
                                <Dropdown.Menu className="noTransformModal">
                                  <Dropdown.Item>
                                    {" "}
                                    <>
                                      {interpretation.qnInterpretationConditions
                                        .find((obj) => obj.seqNum == 1)
                                        ?.operator.includes("Ratio") ? (
                                        <RatioTextField
                                          name={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 1
                                            )?.sourceField
                                          }
                                          value={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 1
                                            )?.valueRHS
                                          }
                                          onChange={(value) =>
                                            updateInputDecision(
                                              interpretation,
                                              index,
                                              1,
                                              value
                                            )
                                          }
                                        />
                                      ) : (
                                        <Form.Control
                                          className="decisionTableCellInput"
                                          placeholder="Enter Value"
                                          name={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 1
                                            )?.sourceField
                                          }
                                          value={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 1
                                            )?.valueRHS
                                          }
                                          onChange={(event) =>
                                            updateInputDecision(
                                              interpretation,
                                              index,
                                              1,
                                              event.target.value
                                            )
                                          }
                                        />
                                      )}
                                    </>
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    name={
                                      interpretation.qnInterpretationConditions.find(
                                        (obj) => obj.seqNum == 1
                                      )?.sourceField
                                    }
                                    onClick={(event) =>
                                      updateInputDecision(
                                        interpretation,
                                        index,
                                        1,
                                        null
                                      )
                                    }
                                  >
                                    Ignore
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>

                            <td
                              style={{
                                backgroundColor:
                                  interpretation.qnInterpretationConditions.find(
                                    (obj) => obj.seqNum == 2
                                  )?.valueRHS === null
                                    ? "#d1d1d1"
                                    : "white",
                              }}
                            >
                              {" "}
                              <Dropdown as={ButtonGroup} autoClose="outside">
                                {interpretation.qnInterpretationConditions.find(
                                  (obj) => obj.seqNum == 2
                                )?.valueRHS === null ? (
                                  <>
                                    <Form.Text className="decisionTableCellInput ">
                                      Null
                                    </Form.Text>
                                    <Dropdown.Toggle
                                      as={CustomToggleNull}
                                      split
                                    />
                                  </>
                                ) : (
                                  <>
                                    {interpretation.qnInterpretationConditions
                                      .find((obj) => obj.seqNum == 2)
                                      ?.operator.includes("Ratio") ? (
                                      <>
                                        <RatioTextField
                                          required={validateDecisionTable()}
                                          name={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 2
                                            )?.sourceField
                                          }
                                          value={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 2
                                            )?.valueRHS
                                          }
                                          onChange={(value) =>
                                            updateInputDecision(
                                              interpretation,
                                              index,
                                              2,
                                              value
                                            )
                                          }
                                        />
                                        <Dropdown.Toggle
                                          as={CustomToggleRatio}
                                          split
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <Form.Control
                                          required={validateDecisionTable()}
                                          className="decisionTableCellInput"
                                          placeholder="Enter Value"
                                          name={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 2
                                            )?.sourceField
                                          }
                                          value={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 2
                                            )?.valueRHS
                                          }
                                          onChange={(event) =>
                                            updateInputDecision(
                                              interpretation,
                                              index,
                                              2,
                                              event.target.value
                                            )
                                          }
                                        />
                                        <Dropdown.Toggle
                                          as={CustomToggleText}
                                          split
                                        />
                                      </>
                                    )}
                                  </>
                                )}
                                <Dropdown.Menu className="noTransformModal">
                                  <Dropdown.Item>
                                    <>
                                      {interpretation.qnInterpretationConditions
                                        .find((obj) => obj.seqNum == 2)
                                        ?.operator.includes("Ratio") ? (
                                        <RatioTextField
                                          name={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 2
                                            )?.sourceField
                                          }
                                          value={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 2
                                            )?.valueRHS
                                          }
                                          onChange={(value) => {
                                            updateInputDecision(
                                              interpretation,
                                              index,
                                              2,
                                              value
                                            );
                                          }}
                                        />
                                      ) : (
                                        <Form.Control
                                          className="decisionTableCellInput"
                                          placeholder="Enter Value"
                                          name={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 2
                                            )?.sourceField
                                          }
                                          value={
                                            interpretation.qnInterpretationConditions.find(
                                              (obj) => obj.seqNum == 2
                                            )?.valueRHS
                                          }
                                          onChange={(event) =>
                                            updateInputDecision(
                                              interpretation,
                                              index,
                                              2,
                                              event.target.value
                                            )
                                          }
                                        />
                                      )}
                                    </>
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    name={
                                      interpretation.qnInterpretationConditions.find(
                                        (obj) => obj.seqNum == 2
                                      )?.sourceField
                                    }
                                    onClick={(event) =>
                                      updateInputDecision(
                                        interpretation,
                                        index,
                                        2,
                                        null
                                      )
                                    }
                                  >
                                    Ignore
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                            <td>
                              <Form.Select
                                required={validateDecisionTable()}
                                name="column999"
                                className="decisionTableCellInput"
                                aria-label="Object"
                                value={
                                  interpretation.qnInterpretationActions[0]
                                    .valueRHS
                                }
                                onChange={(event) =>
                                  updateInputDecisionOutput(
                                    interpretation,
                                    index,
                                    event.target.value
                                  )
                                }
                              >
                                <option hidden value="">
                                  Select
                                </option>
                                {TEST_RESULT_OPTIONS.sort((a, b) =>
                                  a > b ? 1 : -1
                                ).map((entry) => {
                                  return (
                                    <option key={entry} value={entry}>
                                      {entry}
                                    </option>
                                  );
                                })}
                              </Form.Select>
                            </td>

                            <td>
                              <Button
                                onClick={(event) =>
                                  removeRow(interpretation, index)
                                }
                                className="removeButtonStyle generalFont flexCenter "
                              >
                                {" "}
                                <img
                                  src={`${validateAndSanitizeUrl(S3_URL)}delete-icon.png`}
                                  className="buttonIcon"
                                  alt=""
                                ></img>
                              </Button>
                            </td>
                          </tr>
                        );
                      })}

                    <tr>
                      <td colSpan="100%">
                        <Button
                          className="tableAddFieldBtn flexCenter generalFont"
                          onClick={() => addFields()}
                        >
                          <img
                            style={{ marginRight: "10px" }}
                            src={`${validateAndSanitizeUrl(S3_URL)}plus-vector-blue-icon.png`}
                            className="ruleModalAddIcon"
                            alt=""
                          ></img>
                          Add Field
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Row>
          )}

          <Row>
            <Form.Group className="mb-3" controlId="form.comments">
              <Form.Label className="modalLabel generalFont">
                Admin Comments
              </Form.Label>
              <Form.Control
                className="modalInput generalFont"
                as="textarea"
                rows={3}
                maxLength={250}
                value={form.comments}
                onChange={(e) => setField("comments", e.target.value)}
              />
            </Form.Group>
          </Row>
        </Modal.Body>
        <Modal.Footer className="modalFooterContainer">
          <i className="text-danger">{errorText}</i>
          <Button
            type="submit"
            className="modalAddUpdateStyle generalFont flexCenter"
          >
            <img
              src={`${validateAndSanitizeUrl(S3_URL)}plus-icon.png`}
              alt=""
              style={{ marginRight: "10px" }}
            ></img>
            {isAddNew ? "Add" : "Update"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ReportingLOINCAddUpdateModal;
